import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useLocation } from 'react-router';
import { type CollapsedController } from '../collapsed-controller-context';
import getResizedSideMenuState from '../utils/get-resized-side-menu-state';
import { INITIAL_SIDE_MENU_STATE } from '../utils/side-menu-state';
import { useGetUserQuery, useUpdateSideMenuMutation } from '@/app/services/solution';

const useCollapsedControllerValue = (): CollapsedController => {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const userQuery = useGetUserQuery(void 0, { skip: !isAuthenticated });
  const [updateSideMenu] = useUpdateSideMenuMutation();

  const [sideMenuState, setSideMenuState] = useState(() => getResizedSideMenuState(INITIAL_SIDE_MENU_STATE));

  useEffect(() => {
    queueMicrotask(() => {
      setSideMenuState((previous) => ({ ...previous, sideMenuKey: previous.sideMenuKey + 1 }));
    });
  }, [location]);

  useEffect(() => {
    const onResize = (): (() => void) => {
      const timer = setTimeout(() => {
        setSideMenuState(getResizedSideMenuState);
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    const isSideMenuOpen = userQuery.data?.isSideMenuOpen;

    if (typeof isSideMenuOpen !== 'boolean') {
      return;
    }

    queueMicrotask(() => {
      setSideMenuState((previous) => {
        const collapsed = !isSideMenuOpen;

        return previous.collapsed === collapsed
          ? previous
          : { ...previous, collapsed, sideMenuKey: previous.sideMenuKey + 1 };
      });
    });
  }, [userQuery.data?.isSideMenuOpen]);

  const toggleSideMenu = useCallback((): void => {
    setSideMenuState((previous) => {
      void updateSideMenu({ isSideMenuOpen: previous.collapsed });
      return { ...previous, collapsed: !previous.collapsed };
    });
  }, [updateSideMenu]);

  return useMemo(() => ({ ...sideMenuState, toggleSideMenu }), [sideMenuState, toggleSideMenu]);
};

export default useCollapsedControllerValue;
